import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LossRunReviewViewModel, PendingRequestGridViewModel, PendingSubmissionRequestREQ, RenewalPayrollRequestViewModel, SubmissionReviewViewModel } from '@program-suite/data-common/program';
import { RCIPWizardViewModel } from '@program-suite/data-common';

@Injectable({
  providedIn: 'root',
})
export class PendingRequestService {
  http = inject(HttpClient);

  /**
   * Get pending requests
   * @param params - Criteria for filtering pending requests
   * @returns 200 - OK(PendingRequestGridViewModel)
   * @throws Error if the request fails
   */
  async getPendingRequests(params: any): Promise<PendingRequestGridViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<PendingRequestGridViewModel>('pendingrequest', {
          params,
          responseType: 'json',
        })
      );
    } catch (error) {
      throw new Error('Failed to fetch pending requests. Please try again.');
    }
  }

  /**
   * Get submission review by submission GUID
   * @param submissionGuid - The GUID of the submission
   * @returns 200 - OK(SubmissionReviewViewModel)
   * @throws Error if the request fails or submission is not found
   */
  async getSubmissionReview(submissionGuid: string): Promise<SubmissionReviewViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<SubmissionReviewViewModel>(`submission/submission-review/${submissionGuid}`, {
          responseType: 'json',
        })
      );
    } catch (error) {
      throw new Error('Failed to fetch submission review. Please try again.');
    }
  }

  async getWizard(submissionGuid: string): Promise<RCIPWizardViewModel> {
    return await firstValueFrom(this.http.get<RCIPWizardViewModel>(`RCIPWizard/${submissionGuid}`));
  }

  // Not wrapped like the others: a 400 carries the rating error to show the underwriter
  async updateRenewalPayroll(submissionGuid: string, payroll: RenewalPayrollRequestViewModel): Promise<void> {
    await firstValueFrom(this.http.put(`pendingrequest/renewal-payroll/${submissionGuid}`, payroll));
  }

  async issueLossRunRequest(formData: FormData): Promise<any> {
    try {
      return await firstValueFrom(
        this.http.post<any>('lossruns/issue', formData)
      );
    } catch (error) {
      throw new Error('Failed to issue loss run request');
    }
  } 

   async getLossRunReview(lossRunRequestGuid: string): Promise<LossRunReviewViewModel> {
    try {
      return await firstValueFrom(
        this.http.get<LossRunReviewViewModel>(`lossruns/review/${lossRunRequestGuid}`, {
          responseType: 'json',
        })
      );
    } catch (error) {
      throw new Error('Failed to fetch loss run review.');
    }
  }

  async getPolicyGuid(submissionGuid: string): Promise<string> {
    try {
      return await firstValueFrom(
        this.http.get<string>(`submission/policy-guid/${submissionGuid}`)
      );
    } catch (error) {
      throw new Error('Failed to fetch policy GUID. Please try again.');
    }
  }

  /**
   * Update the status of a pending request
   * @param req - The request object containing the details to update
   * @returns 200 - OK(Any)
   * @throws Error if the request fails
   */
  async updatePendingRequestStatus(req: PendingSubmissionRequestREQ): Promise<any> {
    try {
      return await firstValueFrom(
        this.http.post<any>('pendingrequest/submission-pending-request', req)
      );
    } catch (error) {
      throw new Error('Failed to update pending request status. Please try again.');
    }
  }
}